import { useRequest } from '@Api';
import { DROPDOWN_ACTION } from '@Constants';
import { useEffectCustom } from '@Hooks';
import {
	phoneString, postCodeString, replaceComma, replaceDecimal, taxIdString,
} from '@Masks';
import { useFeedback, useTitleBar } from '@Providers';
import {
	getCitiesByStateId,
	getCountries,
	getStatesByCountryId,
	getChainStores,
	addOrganization,
	getOrganizationById,
	editOrganization,
} from '@Requests';
import { AppRoutes } from '@Routes';
import {
	IChainStore, ICities, ICountry, IOrganizationReq, IStates,
} from '@Types';
import { FormikErrors, FormikHelpers } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CreateOrganizationPage from './CreateOrganizationPage';

const CreateOrganization = () => {
	const { setTitle, setGlobalLoading } = useTitleBar();
	const { t } = useTranslation('translations');
	const { id } = useParams();
	const { addToast, addDialog } = useFeedback();
	const navigate = useNavigate();

	const [initialValues, setInitialValues] = useState<IOrganizationReq>({
		taxId: '',
		phone: '',
		email: '',
		address: '',
		addressComplement: '',
		postalCode: '',
		neighborhood: '',
		latitude: '',
		longitude: '',
		fantasyName: '',
		alternativeName: '',
		officialName: '',
		cityId: {} as ICities,
		stateId: {} as IStates,
		countryId: {} as ICountry,
		establishmentNumber: 0,
		chainStoreId: {} as IChainStore,
	});

	const [countries, setCountries] = useState<ICountry[]>([]);
	const [states, setStates] = useState<IStates[]| []>([]);
	const [cities, setCities] = useState<ICities[]| []>([]);
	const [selectedPlaces] = useState({
		country: 1,
		state: 1,
	});
	const [isEdit, setEdit] = useState(false);

	const chainStores = useRequest(([params]) => getChainStores(params));

	const getStates = async (countryId: number) => {
		const mStates = await getStatesByCountryId(countryId);
		return mStates;
	};

	const getCities = async (stateId: number) => {
		const mCities = await getCitiesByStateId(stateId);
		return mCities;
	};

	const getDropdowns = async () => {
		const mCountries = await getCountries();
		const mStates = await getStates(selectedPlaces.country);
		const mCities = await getCities(selectedPlaces.state);
		setCountries(mCountries.data);
		setStates(mStates.data);
		setCities(mCities.data);
		setInitialValues({
			...initialValues,
			cityId: mCities.data[0],
			countryId: mCountries.data[0],
			stateId: mStates.data[0],
		});
	};

	const getOrganizations = async () => {
		setGlobalLoading(true);
		getOrganizationById(id ?? '').then(async ({ data }) => {
			const mCountries = await getCountries();
			const mStates = await getStates(data.country.id);
			const mCities = await getCities(data.state.id);
			setCountries(mCountries.data);
			setStates(mStates.data);
			setCities(mCities.data);
			setInitialValues({
				taxId: taxIdString(data.taxId),
				phone: phoneString(data.phone) || '',
				email: data.email,
				address: data.address,
				addressComplement: data.addressComplement,
				postalCode: postCodeString(data.postalCode),
				neighborhood: data.neighborhood,
				latitude: replaceDecimal(data.latitude),
				longitude: replaceDecimal(data.longitude),
				fantasyName: data.fantasyName,
				alternativeName: data.fantasyName,
				officialName: data.officialName,
				cityId: data.city,
				stateId: data.state,
				countryId: data.country,
				establishmentNumber: data.establishmentNumber,
				chainStoreId: data.chainStore,
			});
		})
			.catch((err) => {
				addToast({ message: err.response.data.message, error: true });
			})
			.finally(() => setGlobalLoading(false));
	};

	useEffectCustom(() => {
		const getData = () => {
			const listOptions = {
				page: 1,
				size: 1000,
				search: '',
			};
			chainStores.execute(listOptions);
			if (id) {
				setTitle(`${t('organizationDetail')}`);
				getOrganizations();
			} else {
				setEdit(true);
				getDropdowns();
				setTitle(`${t('new')} ${t('organization')}`);
			}
		};
		getData();
	}, [id]);

	const validation = (organizationValues: IOrganizationReq) => {
		const errors = {} as FormikErrors<IOrganizationReq>;
		const lat = replaceComma(organizationValues.latitude);
		const long = replaceComma(organizationValues.longitude);
		if (!organizationValues.officialName) {
			errors.officialName = t('emptyField');
		}
		if (!organizationValues.fantasyName) {
			errors.fantasyName = t('emptyField');
		}
		if (!organizationValues.alternativeName) {
			errors.alternativeName = t('emptyField');
		}
		if (typeof organizationValues.establishmentNumber !== 'number') {
			errors.establishmentNumber = t('emptyField');
		}
		if (!organizationValues.taxId) {
			errors.taxId = t('emptyField');
		}
		if (!organizationValues.email) {
			errors.email = t('emptyField');
		} else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(organizationValues.email)) {
			errors.email = t('invalidEmail');
		}
		if (!organizationValues.phone) {
			errors.phone = t('emptyField');
		}
		if (!organizationValues.address) {
			errors.address = t('emptyField');
		}
		if (!organizationValues.postalCode) {
			errors.postalCode = t('emptyField');
		}
		if (!organizationValues.latitude) {
			errors.latitude = t('emptyField');
		}
		if (organizationValues.latitude.length < 6) {
			errors.latitude = t('lengthError');
		}
		if (organizationValues.longitude.length < 6) {
			errors.longitude = t('lengthError');
		}
		if (lat > 90) {
			errors.latitude = t('coordError').format(90);
		}
		if (long > 180) {
			errors.longitude = t('coordError').format(180);
		}
		if (!organizationValues.longitude) {
			errors.longitude = t('emptyField');
		}
		if (!organizationValues.countryId) {
			errors.countryId = t('emptyField');
		}
		return errors;
	};

	const getDropdownById = async (action: string, dropdownId: number) => {
		switch (action) {
		case DROPDOWN_ACTION.COUNTRY:
		{
			const mStates = await getStates(dropdownId);
			setStates(mStates.data);
			if (dropdownId !== 1) {
				setCities([]);
			}
			break;
		}
		case DROPDOWN_ACTION.STATE:
		{
			const mCities = await getCities(dropdownId);
			setCities(mCities.data);
			break;
		}
		default:
			break;
		}
	};

	const submit = (
		organizationFormValues: IOrganizationReq,
		formikActions: FormikHelpers<IOrganizationReq>,
	) => {
		if (id) {
			editOrganization(id, organizationFormValues)
				.then((res) => {
					addToast({ error: false, message: res.message });
					navigate(AppRoutes.ORGANIZATIONS);
				})
				.catch(({ response }) => addDialog({
					title: response.data.title,
					message: response.data.message,
					error: true,
				}))
				.finally(() => formikActions.setSubmitting(false));
		} else {
			addOrganization(organizationFormValues)
				.then((res) => {
					addToast({ error: false, message: res.message });
					navigate(AppRoutes.ORGANIZATIONS);
				})
				.catch(({ response }) => {
					addDialog({
						title: response.data.title,
						message: response.data.message,
						error: true,
					});
				})
				.finally(() => formikActions.setSubmitting(false));
		}
	};

	const handleEditClick = () => {
		setTitle(`${t('edit')} ${t('organization')}`);
		setEdit(true);
	};

	const handleBackClick = () => {
		navigate(AppRoutes.ORGANIZATIONS);
	};

	return (
		<CreateOrganizationPage
			organization={initialValues}
			countries={countries}
			states={states}
			cities={cities}
			validate={validation}
			chainStores={chainStores.data?.data}
			requestById={getDropdownById}
			submit={submit}
			isEdit={isEdit}
			handleBackClick={handleBackClick}
			handleEditClick={handleEditClick}
		/>
	);
};

export default CreateOrganization;
